import { BrowserRouter } from 'react-router-dom';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import client, { config } from './config/wagmi';

import { ConnectKitProvider } from 'connectkit';
import { Toaster } from 'sonner';
import { ApolloProvider } from '@apollo/client';
import AppRouter from './AppRouter';
import { useEffect } from 'react';

const queryClient = new QueryClient();

// Check if error message is a WalletConnect connection error that should be suppressed
function isWalletConnectError(message: string): boolean {
  return (
    message.includes('Connection interrupted') ||
    message.includes('WebSocket connection failed') ||
    message.includes('pairing topic') ||
    message.includes('while trying to subscribe') ||
    message.includes('Pairing expired') ||
    message.includes('Missing or invalid')
  );
}

// Suppress WalletConnect connection errors that occur during normal operation
function useWalletConnectErrorSuppression() {
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      const message = args.map(arg => arg?.toString?.() || '').join(' ');
      if (isWalletConnectError(message)) {
        return;
      }
      originalError.apply(console, args);
    };

    // Handle unhandled promise rejections from WalletConnect
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || event.reason?.toString() || '';
      if (isWalletConnectError(message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    // Handle error events from WalletConnect (capture phase for React Error Overlay)
    const handleError = (event: ErrorEvent) => {
      const message = event.message || event.error?.message || '';
      if (isWalletConnectError(message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return true;
      }
    };

    // Use capture phase to intercept before React Error Overlay
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
    window.addEventListener('error', handleError, true);

    return () => {
      console.error = originalError;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
      window.removeEventListener('error', handleError, true);
    };
  }, []);
}

function App() {
  useWalletConnectErrorSuppression();

  return (
    <BrowserRouter>
      <ApolloProvider client={client}>
        <WagmiProvider config={config}>
          <QueryClientProvider client={queryClient}>
            <ConnectKitProvider>
              <AppRouter />
              <Toaster position="bottom-right" />
            </ConnectKitProvider>
          </QueryClientProvider>
        </WagmiProvider>
      </ApolloProvider>
    </BrowserRouter>
  );
}

export default App;
